import { z } from 'zod';

/** Identificador UUIDv7 — string opaca para o cliente. */
export const idSchema = z.string().min(1).max(64);

export const idParamSchema = z.object({ id: idSchema });

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(24),
});

export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export interface Paginated<T> {
  readonly items: readonly T[];
  readonly page: number;
  readonly perPage: number;
  readonly total: number;
  readonly totalPages: number;
}

/** Data-calendário `yyyy-mm-dd`, sem hora nem fuso. */
export const dateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use o formato AAAA-MM-DD')
  .refine((value) => !Number.isNaN(Date.parse(`${value}T00:00:00Z`)), 'Data inválida');

/** Valor monetário em centavos inteiros — ADR 009. */
export const centsSchema = z
  .number()
  .int('Valor deve ser inteiro em centavos')
  .min(0, 'Valor não pode ser negativo')
  .max(Number.MAX_SAFE_INTEGER);

export const latitudeSchema = z.number().min(-90).max(90);
export const longitudeSchema = z.number().min(-180).max(180);

/*
 * Listas num único parâmetro de query, separadas por vírgula.
 *
 * `?fiscalIds=a,b` em vez de `?fiscalIds=a&fiscalIds=b`: a repetição de chave
 * só vira array se o parser da query colaborar, e esse comportamento muda entre
 * servidor e cliente. A vírgula é explícita e não depende de ninguém.
 */
const splitCsv = (value: string): string[] =>
  value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '');

/** Lista de identificadores. */
export const csvIdsSchema = z
  .string()
  .transform(splitCsv)
  .pipe(z.array(idSchema).max(50))
  .optional();

/**
 * Lista de valores de um conjunto fixo.
 *
 * A conversão declara o tipo estreito e o `refine` logo abaixo é quem garante
 * isso em execução — separar as duas etapas evita brigar com a inferência do
 * `pipe` sobre uniões literais.
 */
export const csvEnumSchema = <const T extends readonly [string, ...string[]]>(values: T) =>
  z
    .string()
    .transform((value) => splitCsv(value) as T[number][])
    .refine((list) => list.every((item) => (values as readonly string[]).includes(item)), {
      message: `Valores aceitos: ${values.join(', ')}`,
    })
    .optional();

/**
 * Texto de formulário: normaliza espaços e converte vazio em `undefined`,
 * para que um campo em branco não vire string vazia no banco.
 */
export const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? undefined : value))
    .optional();
